// T6-BOARD-NOTE: a `board_note` approval's two diffs side by side (universal-task plan §b.3: "the
// approval shows both diffs") — the note as it is now against the note proposed, line by line,
// and each rule change (a new rule by its name and parts; a changed, switched or deleted rule by
// the sentence it has now). The approver decides both at once.
import { Badge } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { noteQuery } from "../work/board-settings/how-it-works-tab";

type RuleChange = {
  op: "create" | "update" | "toggle" | "delete";
  automation_id: string | null;
  enabled?: boolean;
  spec?: {
    name?: string;
    trigger?: { type?: string };
    actions?: { type?: string }[];
  };
};
export type BoardNoteChangePayload = {
  board_id: string;
  body_markdown: string;
  rules: RuleChange[];
  wake_window_minutes?: number;
};

/** Line diff (LCS): each line of `a` and `b` as kept, removed or added, in order. */
export function lineDiff(
  a: string,
  b: string,
): { kind: "same" | "removed" | "added"; text: string }[] {
  const x = a === "" ? [] : a.split("\n");
  const y = b === "" ? [] : b.split("\n");
  const m = x.length;
  const n = y.length;
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--)
    for (let j = n - 1; j >= 0; j--)
      (lcs[i] as number[])[j] =
        x[i] === y[j]
          ? ((lcs[i + 1] as number[])[j + 1] as number) + 1
          : Math.max((lcs[i + 1] as number[])[j] as number, (lcs[i] as number[])[j + 1] as number);
  const out: { kind: "same" | "removed" | "added"; text: string }[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (x[i] === y[j]) {
      out.push({ kind: "same", text: x[i] as string });
      i++;
      j++;
    } else if (((lcs[i + 1] as number[])[j] as number) >= ((lcs[i] as number[])[j + 1] as number)) {
      out.push({ kind: "removed", text: x[i++] as string });
    } else out.push({ kind: "added", text: y[j++] as string });
  }
  while (i < m) out.push({ kind: "removed", text: x[i++] as string });
  while (j < n) out.push({ kind: "added", text: y[j++] as string });
  return out;
}

const OP_LABEL: Record<RuleChange["op"], string> = {
  create: "New rule",
  update: "Changed rule",
  toggle: "Switched",
  delete: "Deleted rule",
};

export function BoardNoteChange({
  api,
  co,
  change,
}: {
  api: Api;
  co: string;
  change: BoardNoteChangePayload;
}) {
  const now = useQuery(noteQuery(api, co, change.board_id));
  const before = now.data?.body_markdown ?? "";
  const sentence = (id: string | null) =>
    now.data?.rules.find((r) => r.id === id)?.sentence ?? "A rule you cannot see";
  return (
    <div data-slot="board-note-change" className="flex flex-col gap-4 text-sm">
      <div>
        <h4 className="m-0 mb-1 text-xs font-semibold text-muted-foreground">Note</h4>
        <pre className="m-0 overflow-x-auto rounded-md border border-subtle p-2 font-mono text-[12px] leading-relaxed whitespace-pre-wrap">
          {lineDiff(before, change.body_markdown).map((l, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: a diff's lines repeat; their place is their identity
              key={i}
              data-diff={l.kind}
              className={
                l.kind === "added"
                  ? "bg-success-soft text-success-text"
                  : l.kind === "removed"
                    ? "bg-danger-soft text-danger-text line-through"
                    : "text-muted-foreground"
              }
            >
              {l.kind === "added" ? "+ " : l.kind === "removed" ? "− " : "  "}
              {l.text || " "}
            </div>
          ))}
        </pre>
      </div>
      {change.rules.length ? (
        <div>
          <h4 className="m-0 mb-1 text-xs font-semibold text-muted-foreground">Rules</h4>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {change.rules.map((r, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: the changes are an ordered list
              <li key={i} className="flex items-start gap-2">
                <Badge variant={r.op === "delete" ? "danger" : "info"}>
                  {r.op === "toggle"
                    ? r.enabled
                      ? "Switched on"
                      : "Switched off"
                    : OP_LABEL[r.op]}
                </Badge>
                <span className="min-w-0">
                  {r.op === "create"
                    ? `${r.spec?.name ?? "Unnamed"}: when ${r.spec?.trigger?.type ?? "?"}, ${(r.spec?.actions ?? []).map((a) => a.type).join(", ") || "nothing"}`
                    : sentence(r.automation_id)}
                  {r.op === "update" && r.spec?.name ? ` → ${r.spec.name}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {change.wake_window_minutes ? (
        <p className="m-0">
          Checks the board every {change.wake_window_minutes} minutes
          {now.data ? ` (now ${now.data.wake_window_minutes})` : ""}.
        </p>
      ) : null}
    </div>
  );
}
